import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Chip,
  SelectChangeEvent,
  InputAdornment,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import MarkdownRenderer from "../../MarkdownRenderer";
import styles from "./training-search-form.module.scss";

type TrainingSearchErrorFormProps = {
  parsedContent: any;
  messageId?: string;
};

export default function TrainingSearchErrorForm({
  parsedContent,
  messageId,
}: TrainingSearchErrorFormProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Get current agent and chat from Redux
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const params = new URLSearchParams(location.search);
  const sessionId = params.get("sessionId");
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  // Extract question from parsedContent
  const data = parsedContent?.finalAnswer || parsedContent;
  const question = data?.question || parsedContent?.question || "";

  // Check if this is the latest message to show the form
  const latestAgentMessage = useMemo(() => {
    return [...currentChat]
      .reverse()
      .find((message) => message.sender === "agent");
  }, [currentChat]);

  const isLatestMessage = latestAgentMessage?.id === messageId;

  // Form state
  const [budget, setBudget] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [mode, setMode] = useState<string[]>([]);
  const [language, setLanguage] = useState<string[]>([]);
  const [trainingLocation, setTrainingLocation] = useState("");
  const [certificationPreference, setCertificationPreference] = useState("");
  const [learningCapacity, setLearningCapacity] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check if form has been submitted or if there's a newer message
  const isFormDisabled = isSubmitted || !isLatestMessage;

  // Check if required fields are filled
  const isFormValid = timeframe.trim() !== "" && trainingLocation.trim() !== "";

  const handleSubmit = useCallback(() => {
    if (!isFormValid || !currentAgent || isFormDisabled) return;

    // Build the submission payload
    const formDataArray = [
      {
        key: "Budget",
        question: "",
        answer: budget.trim() || "",
      },
      {
        key: "Timeframe",
        question: "",
        answer: timeframe,
      },
      {
        key: "Mode",
        question: "",
        answer: mode,
      },
      {
        key: "Language",
        question: "",
        answer: language,
      },
      {
        key: "Location",
        question: "",
        answer: trainingLocation.trim() || "",
      },
      {
        key: "Certification_Preference",
        question: "",
        answer: certificationPreference || "",
      },
      {
        key: "Learning_Capacity",
        question: "",
        answer: learningCapacity.trim() || "",
      },
    ];

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    const payload: any = {
      agentId: currentAgent.id,
      message: JSON.stringify(formDataArray),
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    // Mark form as submitted before dispatching
    setIsSubmitted(true);
    dispatch(sendMessageToAgent(payload));
  }, [
    isFormValid,
    currentAgent,
    isFormDisabled,
    budget,
    timeframe,
    mode,
    language,
    trainingLocation,
    certificationPreference,
    learningCapacity,
    sessionId,
    dispatch,
  ]);

  // Shared styles
  const fieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        backgroundColor: isFormDisabled ? "#f9fafb" : "#fff",
        fontSize: "14px",
        "& fieldset": { borderColor: "#d0e8e4" },
        "&:hover fieldset": {
          borderColor: isFormDisabled ? "#d0e8e4" : "#00a888",
        },
        "&.Mui-focused fieldset": { borderColor: "#00a888" },
      },
      "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "#6b7280",
      },
    }),
    [isFormDisabled],
  );

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  const labelSx = {
    color: "#9ca3af",
    fontWeight: 400,
    fontSize: "12px",
  };

  return (
    <Box className={styles.conversationContainer}>
      {/* Question/Prompt - always visible */}
      {/* {question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionIcon}>
            <InfoOutlinedIcon sx={{ color: "#6b7280", fontSize: 18 }} />
          </Box>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={question} />
          </Box>
        </Box>
      )} */}

      {/* Form - always visible */}
      <Box className={styles.formContainer}>
        {/* Form Header */}
        <Box className={styles.formHeader}>
          <Typography className={styles.formEyebrow}>
            Training Search Preferences
          </Typography>
          <Typography className={styles.formTitle}>
            Refine your training search
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
            Provide additional preferences to help us find the most suitable
            training courses for you.
          </Typography>
        </Box>

        {/* Form Fields */}
        <Box className={styles.formFields}>
          {/* Timeframe Field - REQUIRED */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              What is your preferred timeframe for completing the training?
              <span style={{ color: "#dc2626", fontWeight: 600 }}>
                {" "}
                (Required)
              </span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              Used to check feasibility based on estimated course duration. We
              prioritize quality over short duration.
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                displayEmpty
                disabled={isFormDisabled}
              >
                <MenuItem value="" disabled>
                  Select timeframe
                </MenuItem>
                <MenuItem value="1-2 weeks">1-2 weeks</MenuItem>
                <MenuItem value="1 month">1 month</MenuItem>
                <MenuItem value="2-3 months">2-3 months</MenuItem>
                <MenuItem value="3-6 months">3-6 months</MenuItem>
                <MenuItem value="6+ months">6+ months</MenuItem>
                <MenuItem value="Flexible">Flexible</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Location Field - REQUIRED */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              What is your preferred location for in-person training?
              <span style={{ color: "#dc2626", fontWeight: 600 }}>
                {" "}
                (Required)
              </span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              Relevant for classroom/in-person trainings. Less important for
              online courses.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={trainingLocation}
              onChange={(e) => setTrainingLocation(e.target.value)}
              placeholder="e.g., Dubai, Abu Dhabi, or Remote"
              sx={fieldSx}
              disabled={isFormDisabled}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Budget Field - RECOMMENDED */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              What is your budget for training?
              <span style={{ color: "#f59e0b", fontWeight: 600 }}>
                {" "}
                (Recommended)
              </span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              Used at overall plan/combination level. If course cost is
              unavailable, we won't block recommendations.
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="e.g., 5000 AED or 1000-3000 USD"
              sx={fieldSx}
              disabled={isFormDisabled}
              // InputProps={{
              //   startAdornment: (
              //     <InputAdornment position="start">
              //       <span style={{ color: "#6b7280" }}>$</span>
              //     </InputAdornment>
              //   ),
              // }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Mode Field */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              What is your preferred training mode?
              <span style={labelSx}> (Optional)</span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              Select one or more delivery modes if available in course metadata.
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                multiple
                value={mode}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const value = e.target.value;
                  setMode(typeof value === "string" ? value.split(",") : value);
                }}
                displayEmpty
                disabled={isFormDisabled}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span style={{ color: "#9ca3af" }}>
                        Select training mode(s)
                      </span>
                    );
                  }
                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          onDelete={
                            isFormDisabled
                              ? undefined
                              : (e) => {
                                  e.stopPropagation();
                                  setMode((prev) =>
                                    prev.filter((item) => item !== value),
                                  );
                                }
                          }
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            backgroundColor: "#e8f5f3",
                            color: "#00a888",
                            fontWeight: 500,
                            fontSize: "12px",
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
              >
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Classroom">Classroom</MenuItem>
                <MenuItem value="Self-paced">Self-paced</MenuItem>
                <MenuItem value="Instructor-led">Instructor-led</MenuItem>
                <MenuItem value="Hybrid">Hybrid</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Language Field */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              What is your preferred language for training materials?
              <span style={labelSx}> (Optional)</span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              Filter or rank courses by language if metadata is available.
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                multiple
                value={language}
                onChange={(e: SelectChangeEvent<string[]>) => {
                  const value = e.target.value;
                  setLanguage(
                    typeof value === "string" ? value.split(",") : value,
                  );
                }}
                displayEmpty
                disabled={isFormDisabled}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span style={{ color: "#9ca3af" }}>
                        Select language(s)
                      </span>
                    );
                  }
                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          onDelete={
                            isFormDisabled
                              ? undefined
                              : (e) => {
                                  e.stopPropagation();
                                  setLanguage((prev) =>
                                    prev.filter((item) => item !== value),
                                  );
                                }
                          }
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            backgroundColor: "#e8f5f3",
                            color: "#00a888",
                            fontWeight: 500,
                            fontSize: "12px",
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                    },
                  },
                }}
              >
                <MenuItem value="English">English</MenuItem>
                <MenuItem value="Arabic">Arabic</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Certification Preference Field */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              Do you prefer courses with certification?
              <span style={labelSx}> (Optional)</span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              We'll prioritize courses offering certification when requested.
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                value={certificationPreference}
                onChange={(e) => setCertificationPreference(e.target.value)}
                displayEmpty
                disabled={isFormDisabled}
              >
                <MenuItem value="">
                  <span style={{ color: "#9ca3af" }}>Select preference</span>
                </MenuItem>
                <MenuItem value="Required">
                  Required - Only show courses with certification
                </MenuItem>
                <MenuItem value="Preferred">
                  Preferred - Prioritize certified courses
                </MenuItem>
                <MenuItem value="Not Important">
                  Not Important - Certification doesn't matter
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Learning Capacity Field */}
          <Box mb={2.5}>
            <Typography sx={questionSx}>
              How many hours per week can you dedicate to learning?
              <span style={labelSx}> (Optional / Future)</span>
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, mt: -0.5 }}
            >
              More accurate for realistic scheduling based on availability.
            </Typography>
            <FormControl fullWidth size="small" sx={fieldSx}>
              <Select
                value={learningCapacity}
                onChange={(e) => setLearningCapacity(e.target.value)}
                displayEmpty
                disabled={isFormDisabled}
              >
                <MenuItem value="">
                  <span style={{ color: "#9ca3af" }}>
                    Select hours per week
                  </span>
                </MenuItem>
                <MenuItem value="1-5 hours">1-5 hours/week</MenuItem>
                <MenuItem value="5-10 hours">5-10 hours/week</MenuItem>
                <MenuItem value="10-20 hours">10-20 hours/week</MenuItem>
                <MenuItem value="20+ hours">20+ hours/week</MenuItem>
                <MenuItem value="Full-time">
                  Full-time (40+ hours/week)
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box className={styles.formActions}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid || agentLoading || isFormDisabled}
            className={styles.submitButton}
          >
            {agentLoading
              ? "Searching..."
              : isFormDisabled
                ? "Submitted"
                : "Search Trainings"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
